import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { apiClient } from 'Utils/api';
import { useCart } from 'Context/CartContext';
import { useCountry } from 'Context/CountryContext';
import { isExportCountry, countryName, NON_EU_CODE } from 'Utils/euCountries';
import Header from 'View/Component/Header/Header';
import Footer from 'View/Component/Footer/Footer';
import * as S from './CheckoutAddressPage.style';

type DeliveryMethod = 'SHIPPING' | 'PICKUP';
type PickupPayment = 'SEPA' | 'ONSITE';

interface PickupLocation {
  enabled: boolean;
  companyName: string;
  street: string;
  postalCode: string;
  city: string;
  countryCode: string;
  contactHint: { de: string; en: string };
}

interface FormData {
  email: string;
  firstName: string;
  lastName: string;
  phone: string;
  street: string;
  city: string;
  postalCode: string;
  country: string;
  useDifferentShipping: boolean;
  shippingFirstName: string;
  shippingLastName: string;
  shippingStreet: string;
  shippingCity: string;
  shippingPostalCode: string;
  shippingCountry: string;
  countryManual: string;
  shippingCountryManual: string;
  deliveryMethod: DeliveryMethod;
  pickupPayment: PickupPayment;
  customerNote: string;
}

const CheckoutAddressPage: React.FC = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { itemCount, items } = useCart();
  const { countries, country: ctxCountry, setCountry } = useCountry();
  const lang = i18n.language || 'de';
  const maintainedCountryCodes = (countries || [])
    .map((c: any) => c.code)
    .filter((code: string) => code && code !== 'NON_EU');
  // The export notice is only meaningful when the cart actually contains a
  // standard-VAT watch (margin-scheme items keep their price regardless of country).
  const cartHasStandardVat = (items || []).some(
    (it: any) => it.taxScheme === 'STANDARD_VAT_19',
  );
  // Selectable delivery countries: all maintained tax-rate countries (sorted by
  // localized name) plus the explicit "Nicht-EU / Andere (Ausfuhr)" export option.
  const countryOptions = [
    ...maintainedCountryCodes
      .slice()
      .sort((a: string, b: string) => countryName(a, lang).localeCompare(countryName(b, lang), lang)),
    NON_EU_CODE,
  ];
  const [pickup, setPickup] = useState<PickupLocation | null>(null);
  const [formData, setFormData] = useState<FormData>({
    email: '',
    firstName: '',
    lastName: '',
    phone: '',
    street: '',
    city: '',
    postalCode: '',
    country: ctxCountry || 'DE',
    useDifferentShipping: false,
    shippingFirstName: '',
    shippingLastName: '',
    shippingStreet: '',
    shippingCity: '',
    shippingPostalCode: '',
    shippingCountry: ctxCountry || 'DE',
    countryManual: '',
    shippingCountryManual: '',
    deliveryMethod: 'SHIPPING',
    pickupPayment: 'SEPA',
    customerNote: '',
  });

  // Two-way sync with the global delivery country (drives cart/checkout pricing):
  // - Header change -> follow it in the form (so the form select never "springs back").
  // - Form select change -> push to context via the dedicated handlers below.
  // This effect only mirrors header -> form (it never calls setCountry) to avoid loops.
  useEffect(() => {
    setFormData((prev) => {
      if (prev.deliveryMethod === 'SHIPPING' && prev.useDifferentShipping) {
        return prev.shippingCountry === ctxCountry ? prev : { ...prev, shippingCountry: ctxCountry };
      }
      return prev.country === ctxCountry
        ? prev
        : { ...prev, country: ctxCountry, shippingCountry: ctxCountry };
    });
  }, [ctxCountry]);

  const [errors, setErrors] = useState<Partial<Record<keyof FormData, string>>>({});

  useEffect(() => {
    if (itemCount === 0) {
      navigate('/warenkorb');
    }
  }, [itemCount, navigate]);

  // Load pickup location config
  useEffect(() => {
    (async () => {
      try {
        const res = await apiClient.get(`/api/pickup-location`);
        if (res.data?.pickup) setPickup(res.data.pickup);
      } catch (e) {
        /* pickup optional */
      }
    })();
  }, []);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>,
  ) => {
    const { name, value, type } = e.target as HTMLInputElement;
    const checked = (e.target as HTMLInputElement).checked;

    setFormData((prev) => {
      const next = { ...prev, [name]: type === 'checkbox' ? checked : value };
      // Toggling "different shipping address" changes which country drives pricing.
      if (name === 'useDifferentShipping') {
        setCountry(checked ? next.shippingCountry : next.country);
      }
      return next;
    });

    if (errors[name as keyof FormData]) {
      setErrors((prev) => ({ ...prev, [name]: undefined }));
    }
  };

  // Billing country select: drives pricing unless a separate shipping address is used.
  const handleCountryChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const value = e.target.value;
    setFormData((prev) => ({ ...prev, country: value }));
    if (!(formData.deliveryMethod === 'SHIPPING' && formData.useDifferentShipping)) {
      setCountry(value);
    }
    if (errors.country) setErrors((prev) => ({ ...prev, country: undefined }));
  };

  // Shipping country select: drives pricing when a separate shipping address is used.
  const handleShippingCountryChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const value = e.target.value;
    setFormData((prev) => ({ ...prev, shippingCountry: value }));
    if (formData.deliveryMethod === 'SHIPPING' && formData.useDifferentShipping) {
      setCountry(value);
    }
  };

  const setDeliveryMethod = (method: DeliveryMethod) => {
    setFormData((prev) => {
      const useDifferent = method === 'PICKUP' ? false : prev.useDifferentShipping;
      // Pickup / same-address -> billing country drives pricing.
      setCountry(useDifferent ? prev.shippingCountry : prev.country);
      return { ...prev, deliveryMethod: method, useDifferentShipping: useDifferent };
    });
  };

  const validate = (): boolean => {
    const newErrors: Partial<Record<keyof FormData, string>> = {};

    if (!formData.email || !/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = t('checkout.errors.email_invalid');
    }
    if (!formData.firstName) newErrors.firstName = t('checkout.errors.first_name_required');
    if (!formData.lastName) newErrors.lastName = t('checkout.errors.last_name_required');
    if (!formData.street) newErrors.street = t('checkout.errors.street_required');
    if (!formData.city) newErrors.city = t('checkout.errors.city_required');
    if (!formData.postalCode) newErrors.postalCode = t('checkout.errors.postal_required');
    if (formData.country === NON_EU_CODE && !formData.countryManual.trim()) {
      newErrors.countryManual = t('checkout.errors.country_manual_required', { defaultValue: 'Bitte geben Sie Ihr Land an.' });
    }

    if (formData.deliveryMethod === 'SHIPPING' && formData.useDifferentShipping) {
      if (!formData.shippingFirstName) newErrors.shippingFirstName = t('checkout.errors.first_name_required');
      if (!formData.shippingLastName) newErrors.shippingLastName = t('checkout.errors.last_name_required');
      if (!formData.shippingStreet) newErrors.shippingStreet = t('checkout.errors.street_required');
      if (!formData.shippingCity) newErrors.shippingCity = t('checkout.errors.city_required');
      if (!formData.shippingPostalCode) newErrors.shippingPostalCode = t('checkout.errors.postal_required');
      if (formData.shippingCountry === NON_EU_CODE && !formData.shippingCountryManual.trim()) {
        newErrors.shippingCountryManual = t('checkout.errors.country_manual_required', { defaultValue: 'Bitte geben Sie Ihr Land an.' });
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (!validate()) {
      return;
    }

    sessionStorage.setItem('checkoutFormData', JSON.stringify(formData));
    navigate('/checkout/uebersicht');
  };

  return (
    <>
      <Header />
      <S.PageContainer>
        <S.PageTitle>{t('checkout.address_title')}</S.PageTitle>

        <S.FormContainer onSubmit={handleSubmit}>
          <S.Section>
            <S.SectionTitle>{t('checkout.section_contact')}</S.SectionTitle>

            <S.FormGroup>
              <S.Label>{t('checkout.label_email')} *</S.Label>
              <S.Input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                $error={!!errors.email}
                data-testid="checkout-email-input"
              />
              {errors.email && <S.ErrorText>{errors.email}</S.ErrorText>}
            </S.FormGroup>

            <S.FormGroup>
              <S.Label>{t('checkout.label_phone')}</S.Label>
              <S.Input
                type="tel"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                data-testid="checkout-phone-input"
              />
            </S.FormGroup>
          </S.Section>

          {pickup?.enabled && (
            <S.Section>
              <S.SectionTitle>{t('checkout.section_delivery')}</S.SectionTitle>

              <S.DeliveryOptions>
                <S.DeliveryOption
                  type="button"
                  selected={formData.deliveryMethod === 'SHIPPING'}
                  onClick={() => setDeliveryMethod('SHIPPING')}
                  data-testid="delivery-option-shipping"
                >
                  <S.DeliveryRadio selected={formData.deliveryMethod === 'SHIPPING'} />
                  <span>{t('checkout.delivery_shipping')}</span>
                </S.DeliveryOption>

                <S.DeliveryOption
                  type="button"
                  selected={formData.deliveryMethod === 'PICKUP'}
                  onClick={() => setDeliveryMethod('PICKUP')}
                  data-testid="delivery-option-pickup"
                >
                  <S.DeliveryRadio selected={formData.deliveryMethod === 'PICKUP'} />
                  <span>{t('checkout.delivery_pickup')}</span>
                </S.DeliveryOption>
              </S.DeliveryOptions>

              {formData.deliveryMethod === 'PICKUP' && (
                <S.PickupHintBox data-testid="pickup-hint-box">
                  <S.PickupHintTitle>{t('checkout.pickup_hint_title')}</S.PickupHintTitle>
                  <S.PickupHintText>
                    {(pickup.contactHint?.[i18n.language as 'de' | 'en'])
                      || pickup.contactHint?.de
                      || ''}
                  </S.PickupHintText>
                  {(pickup.companyName || pickup.street) && (
                    <S.PickupAddressBlock>
                      <strong>{t('checkout.pickup_address_title')}</strong>
                      {pickup.companyName && <div>{pickup.companyName}</div>}
                      {pickup.street && <div>{pickup.street}</div>}
                      {(pickup.postalCode || pickup.city) && (
                        <div>{pickup.postalCode} {pickup.city}</div>
                      )}
                      {pickup.countryCode && (
                        <div>{t(`checkout.countries.${pickup.countryCode}`, { defaultValue: pickup.countryCode })}</div>
                      )}
                    </S.PickupAddressBlock>
                  )}
                </S.PickupHintBox>
              )}

              {formData.deliveryMethod === 'PICKUP' && (
                <div data-testid="pickup-payment-block" style={{ marginTop: '1.25rem' }}>
                  <S.SectionTitle style={{ fontSize: '1rem' }}>
                    {t('checkout.payment_method_title')}
                  </S.SectionTitle>
                  <S.DeliveryOptions>
                    <S.DeliveryOption
                      type="button"
                      selected={formData.pickupPayment === 'SEPA'}
                      onClick={() =>
                        setFormData((prev) => ({ ...prev, pickupPayment: 'SEPA' }))
                      }
                      data-testid="pickup-payment-sepa"
                    >
                      <S.DeliveryRadio selected={formData.pickupPayment === 'SEPA'} />
                      <span>{t('checkout.payment_method_sepa')}</span>
                    </S.DeliveryOption>

                    <S.DeliveryOption
                      type="button"
                      selected={formData.pickupPayment === 'ONSITE'}
                      onClick={() =>
                        setFormData((prev) => ({ ...prev, pickupPayment: 'ONSITE' }))
                      }
                      data-testid="pickup-payment-onsite"
                    >
                      <S.DeliveryRadio selected={formData.pickupPayment === 'ONSITE'} />
                      <span>{t('checkout.payment_method_onsite')}</span>
                    </S.DeliveryOption>
                  </S.DeliveryOptions>

                  {formData.pickupPayment === 'SEPA' && (
                    <S.PickupHintBox data-testid="pickup-payment-sepa-info" style={{ marginTop: '0.75rem' }}>
                      <S.PickupHintText>{t('checkout.payment_method_sepa_info')}</S.PickupHintText>
                    </S.PickupHintBox>
                  )}

                  {formData.pickupPayment === 'ONSITE' && (
                    <S.PickupHintBox data-testid="pickup-payment-onsite-info" style={{ marginTop: '0.75rem' }}>
                      <S.PickupHintText>{t('checkout.payment_method_onsite_info')}</S.PickupHintText>
                      <ul style={{ margin: '0.5rem 0 0 1.25rem', padding: 0 }}>
                        <li>{t('checkout.payment_method_onsite_option_realtime')}</li>
                        <li>{t('checkout.payment_method_onsite_option_cash')}</li>
                        <li>{t('checkout.payment_method_onsite_option_card')}</li>
                      </ul>
                    </S.PickupHintBox>
                  )}
                </div>
              )}
            </S.Section>
          )}

          <S.Section>
            <S.SectionTitle>{t('checkout.section_billing')}</S.SectionTitle>

            <S.FormRow>
              <S.FormGroup>
                <S.Label>{t('checkout.label_first_name')} *</S.Label>
                <S.Input
                  type="text"
                  name="firstName"
                  value={formData.firstName}
                  onChange={handleChange}
                  $error={!!errors.firstName}
                />
                {errors.firstName && <S.ErrorText>{errors.firstName}</S.ErrorText>}
              </S.FormGroup>

              <S.FormGroup>
                <S.Label>{t('checkout.label_last_name')} *</S.Label>
                <S.Input
                  type="text"
                  name="lastName"
                  value={formData.lastName}
                  onChange={handleChange}
                  $error={!!errors.lastName}
                />
                {errors.lastName && <S.ErrorText>{errors.lastName}</S.ErrorText>}
              </S.FormGroup>
            </S.FormRow>

            <S.FormGroup>
              <S.Label>{t('checkout.label_street')} *</S.Label>
              <S.Input
                type="text"
                name="street"
                value={formData.street}
                onChange={handleChange}
                $error={!!errors.street}
              />
              {errors.street && <S.ErrorText>{errors.street}</S.ErrorText>}
            </S.FormGroup>

            <S.FormRow>
              <S.FormGroup>
                <S.Label>{t('checkout.label_postal_code')} *</S.Label>
                <S.Input
                  type="text"
                  name="postalCode"
                  value={formData.postalCode}
                  onChange={handleChange}
                  $error={!!errors.postalCode}
                />
                {errors.postalCode && <S.ErrorText>{errors.postalCode}</S.ErrorText>}
              </S.FormGroup>

              <S.FormGroup>
                <S.Label>{t('checkout.label_city')} *</S.Label>
                <S.Input
                  type="text"
                  name="city"
                  value={formData.city}
                  onChange={handleChange}
                  $error={!!errors.city}
                />
                {errors.city && <S.ErrorText>{errors.city}</S.ErrorText>}
              </S.FormGroup>
            </S.FormRow>

            <S.FormGroup>
              <S.Label>{t('checkout.label_country')} *</S.Label>
              <S.Select name="country" value={formData.country} onChange={handleCountryChange} data-testid="checkout-country-select">
                {countryOptions.map((code) => (
                  <option key={code} value={code}>
                    {countryName(code, lang)}
                  </option>
                ))}
              </S.Select>
            </S.FormGroup>
            {formData.country === NON_EU_CODE && (
              <S.FormGroup>
                <S.Label>{t('checkout.label_country_manual', { defaultValue: 'Land (bitte angeben)' })} *</S.Label>
                <S.Input
                  type="text"
                  name="countryManual"
                  value={formData.countryManual}
                  onChange={handleChange}
                  $error={!!errors.countryManual}
                  data-testid="checkout-country-manual-input"
                  placeholder={t('checkout.country_manual_placeholder', { defaultValue: 'z. B. Schweiz, USA, VAE …' })}
                />
                {errors.countryManual && <S.ErrorText>{errors.countryManual}</S.ErrorText>}
              </S.FormGroup>
            )}
            {cartHasStandardVat && isExportCountry(formData.country, maintainedCountryCodes) && (
              <S.FormGroup>
                <S.HintNote data-testid="checkout-export-notice">
                  {t('watch_detail.tax_export', { defaultValue: 'Nettopreis (Ausfuhrlieferung) – Einfuhrumsatzsteuer/Zoll trägt der Käufer.' })}
                </S.HintNote>
              </S.FormGroup>
            )}
          </S.Section>

          <S.Section>
            {formData.deliveryMethod === 'SHIPPING' && (
              <S.CheckboxGroup>
                <input
                  type="checkbox"
                  id="differentShipping"
                  name="useDifferentShipping"
                  checked={formData.useDifferentShipping}
                  onChange={handleChange}
                  data-testid="different-shipping-checkbox"
                />
                <label htmlFor="differentShipping">{t('checkout.different_shipping')}</label>
              </S.CheckboxGroup>
            )}

            {formData.deliveryMethod === 'SHIPPING' && formData.useDifferentShipping && (
              <>
                <S.SectionTitle>{t('checkout.section_shipping')}</S.SectionTitle>

                <S.FormRow>
                  <S.FormGroup>
                    <S.Label>{t('checkout.label_first_name')} *</S.Label>
                    <S.Input
                      type="text"
                      name="shippingFirstName"
                      value={formData.shippingFirstName}
                      onChange={handleChange}
                      $error={!!errors.shippingFirstName}
                    />
                    {errors.shippingFirstName && <S.ErrorText>{errors.shippingFirstName}</S.ErrorText>}
                  </S.FormGroup>

                  <S.FormGroup>
                    <S.Label>{t('checkout.label_last_name')} *</S.Label>
                    <S.Input
                      type="text"
                      name="shippingLastName"
                      value={formData.shippingLastName}
                      onChange={handleChange}
                      $error={!!errors.shippingLastName}
                    />
                    {errors.shippingLastName && <S.ErrorText>{errors.shippingLastName}</S.ErrorText>}
                  </S.FormGroup>
                </S.FormRow>

                <S.FormGroup>
                  <S.Label>{t('checkout.label_street')} *</S.Label>
                  <S.Input
                    type="text"
                    name="shippingStreet"
                    value={formData.shippingStreet}
                    onChange={handleChange}
                    $error={!!errors.shippingStreet}
                  />
                  {errors.shippingStreet && <S.ErrorText>{errors.shippingStreet}</S.ErrorText>}
                </S.FormGroup>

                <S.FormRow>
                  <S.FormGroup>
                    <S.Label>{t('checkout.label_postal_code')} *</S.Label>
                    <S.Input
                      type="text"
                      name="shippingPostalCode"
                      value={formData.shippingPostalCode}
                      onChange={handleChange}
                      $error={!!errors.shippingPostalCode}
                    />
                    {errors.shippingPostalCode && <S.ErrorText>{errors.shippingPostalCode}</S.ErrorText>}
                  </S.FormGroup>

                  <S.FormGroup>
                    <S.Label>{t('checkout.label_city')} *</S.Label>
                    <S.Input
                      type="text"
                      name="shippingCity"
                      value={formData.shippingCity}
                      onChange={handleChange}
                      $error={!!errors.shippingCity}
                    />
                    {errors.shippingCity && <S.ErrorText>{errors.shippingCity}</S.ErrorText>}
                  </S.FormGroup>
                </S.FormRow>

                <S.FormGroup>
                  <S.Label>{t('checkout.label_country')} *</S.Label>
                  <S.Select name="shippingCountry" value={formData.shippingCountry} onChange={handleShippingCountryChange} data-testid="checkout-shipping-country-select">
                    {countryOptions.map((code) => (
                      <option key={code} value={code}>
                        {countryName(code, lang)}
                      </option>
                    ))}
                  </S.Select>
                </S.FormGroup>
                {formData.shippingCountry === NON_EU_CODE && (
                  <S.FormGroup>
                    <S.Label>{t('checkout.label_country_manual', { defaultValue: 'Land (bitte angeben)' })} *</S.Label>
                    <S.Input
                      type="text"
                      name="shippingCountryManual"
                      value={formData.shippingCountryManual}
                      onChange={handleChange}
                      $error={!!errors.shippingCountryManual}
                      data-testid="checkout-shipping-country-manual-input"
                      placeholder={t('checkout.country_manual_placeholder', { defaultValue: 'z. B. Schweiz, USA, VAE …' })}
                    />
                    {errors.shippingCountryManual && <S.ErrorText>{errors.shippingCountryManual}</S.ErrorText>}
                  </S.FormGroup>
                )}
                {cartHasStandardVat && isExportCountry(formData.shippingCountry, maintainedCountryCodes) && (
                  <S.FormGroup>
                    <S.HintNote data-testid="checkout-shipping-export-notice">
                      {t('watch_detail.tax_export', { defaultValue: 'Nettopreis (Ausfuhrlieferung) – Einfuhrumsatzsteuer/Zoll trägt der Käufer.' })}
                    </S.HintNote>
                  </S.FormGroup>
                )}
              </>
            )}
          </S.Section>

          <S.Section>
            <S.SectionTitle>{t('checkout.section_note')}</S.SectionTitle>
            <S.FormGroup>
              <S.TextArea
                name="customerNote"
                value={formData.customerNote}
                onChange={handleChange}
                placeholder={t('checkout.note_placeholder')}
                rows={4}
                maxLength={1000}
                data-testid="customer-note-textarea"
              />
            </S.FormGroup>
          </S.Section>

          <S.ButtonGroup>
            <S.BackButton type="button" onClick={() => navigate('/warenkorb')}>
              {t('checkout.back')}
            </S.BackButton>
            <S.SubmitButton type="submit" data-testid="checkout-submit-btn">
              {t('checkout.submit')}
            </S.SubmitButton>
          </S.ButtonGroup>
        </S.FormContainer>
      </S.PageContainer>
      <Footer />
    </>
  );
};

export default CheckoutAddressPage;
